import { clearFailedMessage, selectInteractions } from '@/lib/features/copilotv2/ChatSlice'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { useAppDispatch, useAppSelector } from '@/lib/hooks'
import { getErrorTitle, getRetryButtonText, mapErrorToUserFriendly } from '@/lib/utils/errorMapping'
import {
  AlertCircleIcon,
  CreditCardIcon,
  FileXIcon,
  LockIcon,
  RefreshCcwIcon,
  WifiOffIcon,
} from 'lucide-react'
import Link from 'next/link'

export default function ChatErrorDisplay() {
  const dispatch = useAppDispatch()
  const { error, sendMessage, sessionId } = useCopilotContext()
  const failedMessage = useAppSelector((state) => state.streamingChat.failedMessage)
  const interactions = useAppSelector(selectInteractions)

  if (!error) {
    return null
  }

  const handleRetry = async () => {
    try {
      // Clear the failed message from Redux if it exists
      if (failedMessage) {
        console.debug('[ChatErrorDisplay] Clearing failed message state')
        dispatch(clearFailedMessage())
      }

      console.debug('[ChatErrorDisplay] Triggering continuation from current state', {
        sessionId,
        interactionCount: interactions.length,
        timestamp: new Date().toISOString(),
      })

      // Trigger continuation by sending an empty message with continuation flag
      // Backend will continue processing from the current state without adding a new user message
      await sendMessage('', { isContinuation: true })
    } catch (error) {
      console.error('Failed to retry:', error)
      // Error will be handled by the sendMessage error handling
    }
  }

  const errorMapping = mapErrorToUserFriendly(error)

  // Get appropriate icon based on error category
  const getErrorIcon = () => {
    switch (errorMapping.category) {
      case 'network':
        return <WifiOffIcon className="mb-3 text-red-400" size={36} aria-hidden="true" />
      case 'credits':
        return <CreditCardIcon className="mb-3 text-yellow-400" size={36} aria-hidden="true" />
      case 'upload':
        return <FileXIcon className="mb-3 text-red-400" size={36} aria-hidden="true" />
      case 'auth':
        return <LockIcon className="mb-3 text-red-400" size={36} aria-hidden="true" />
      default:
        return <AlertCircleIcon className="mb-3 text-red-400" size={36} aria-hidden="true" />
    }
  }

  // Get appropriate styling based on error category
  const getErrorStyling = () => {
    switch (errorMapping.category) {
      case 'credits':
        return {
          border: 'border-yellow-600',
          background: 'bg-[rgba(60,45,0,0.55)]',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-400',
        }
      case 'auth':
        return {
          border: 'border-blue-600',
          background: 'bg-[rgba(0,20,40,0.55)]',
          buttonColor: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-400',
        }
      default:
        return {
          border: 'border-red-700',
          background: 'bg-[rgba(40,0,0,0.55)]',
          buttonColor: 'bg-red-600 hover:bg-red-700 focus:ring-red-400',
        }
    }
  }

  const styling = getErrorStyling()
  const title = getErrorTitle(errorMapping.category)
  const retryButtonText = getRetryButtonText(errorMapping.category)

  return (
    <div className="flex w-full justify-center px-6 pb-8">
      <div
        className={`flex max-h-96 w-full flex-col items-center overflow-y-auto rounded-3xl border ${styling.border} ${styling.background} px-8 py-6 shadow-2xl outline-none backdrop-blur-lg`}
        style={{ minHeight: '8rem' }}
      >
        {getErrorIcon()}
        <h2 className="mb-2 text-center text-xl font-bold text-white">{title}</h2>
        <p className="mb-6 text-center text-base text-white/90" style={{ whiteSpace: 'pre-line' }}>
          {errorMapping.userFriendlyMessage}
        </p>

        {/* Show technical details in development or for debugging */}
        {process.env.NODE_ENV === 'development' && (
          <details className="mb-4 w-full">
            <summary className="cursor-pointer text-sm text-white/60 hover:text-white/80">
              Technical Details
            </summary>
            <pre className="mt-2 max-h-32 overflow-auto rounded bg-black/20 p-2 text-xs text-white/70">
              {errorMapping.originalError}
            </pre>
          </details>
        )}

        <div className="flex-grow" />

        {errorMapping.shouldShowRetry && (
          <button
            className={`mt-2 flex w-full items-center justify-center gap-2 rounded-full ${styling.buttonColor} px-6 py-3 text-base font-semibold text-white shadow transition focus:outline-none focus:ring-2`}
            onClick={handleRetry}
            aria-label={retryButtonText}
            style={{ marginTop: 'auto' }}
          >
            <RefreshCcwIcon className="inline-block" size={20} aria-hidden="true" />
            {retryButtonText}
          </button>
        )}

        {!errorMapping.shouldShowRetry && errorMapping.category === 'credits' && (
          <Link
            href="/billing"
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-2 flex w-full items-center justify-center gap-2 rounded-full ${styling.buttonColor} px-6 py-3 text-base font-semibold text-white shadow transition focus:outline-none focus:ring-2`}
            aria-label="Upgrade Plan"
            style={{ marginTop: 'auto' }}
          >
            <CreditCardIcon className="inline-block" size={20} aria-hidden="true" />
            Upgrade Plan
          </Link>
        )}

        {!errorMapping.shouldShowRetry && errorMapping.category === 'auth' && (
          <button
            className={`mt-2 flex w-full items-center justify-center gap-2 rounded-full ${styling.buttonColor} px-6 py-3 text-base font-semibold text-white shadow transition focus:outline-none focus:ring-2`}
            onClick={() => {
              // Refresh the page to trigger re-authentication
              window.location.reload()
            }}
            aria-label="Refresh Page"
            style={{ marginTop: 'auto' }}
          >
            <RefreshCcwIcon className="inline-block" size={20} aria-hidden="true" />
            Refresh Page
          </button>
        )}
      </div>
    </div>
  )
}
